import { app } from './core/App';
import CharacterSpine, { CharacterAnimation, CharacterSkins } from './CharacterSpine';
import { BodyTypes } from './level/LevelManager';
import { RunSettingsHandler } from './level/RunSettingsHandler';
import EntityGem from './level/EntityGem';

export const ActionState = {
  Grounded: 'grounded',
  Dropping: 'dropping',
  Jumping: 'jumping',
  Flying: 'flying',
  Falling: 'falling',
  Gliding: 'gliding'
} as const;
export type ActionStateKeys = keyof typeof ActionState;
export type ActionStateValues = typeof ActionState[ActionStateKeys];

export class Player extends window.SpinePlugin.SpineContainer {
  public static events = {
    Jumped: 'jumped',
    SwitchedCharacter: 'switchedCharacter'
  } as const;

  protected character: CharacterSpine;
  protected playerLayer: Phaser.GameObjects.Layer;
  protected playerDeathLayer: Phaser.GameObjects.Layer;
  protected matterSprite!: Phaser.Physics.Matter.Sprite;
  protected playerRect!: MatterJS.BodyType;
  protected isAlive: boolean;
  protected isLocked: boolean;
  protected isMoving: boolean;
  protected actionState: ActionStateValues;

  protected globalSpeed: number;
  protected speed: number;
  protected maxSpeed: number;
  protected maxFallSpeed: number;
  protected glideSpeedModifier: number;
  protected glideMaxFallSpeed: number;
  protected glideTime: number;
  protected maxGlideTime: number;

  protected isTouchingFloor: boolean;
  protected groundTouchPoints: number;
  protected platformsTouching: string[];

  // protected hasJumpStarted: boolean;
  // protected isJumping: boolean;
  protected maxJumpTime: number;
  protected numJumps: number;
  protected jumpTimer: number;
  protected jumpCooldownTime: number;
  protected jumpCooldownTimer: number;

  protected isSmashing: boolean;
  protected maxSmashTime: number;
  protected smashTimer: number;
  protected canBreakAllSmashables: boolean = false;

  protected droppingGraceTime: number;
  protected droppingTimer: number;

  protected gemMagnet!: MatterJS.BodyType;
  protected magnetStrength: number;
  protected magnetisedGems: Set<EntityGem>;

  constructor(scene: Phaser.Scene, x: number, y: number, speed: number, playerLayer: Phaser.GameObjects.Layer, playerDeathLayer: Phaser.GameObjects.Layer) {
    super(scene, scene.sys.spine, x, y);

    this.playerLayer = playerLayer;
    this.playerDeathLayer = playerDeathLayer;

    this.character = new CharacterSpine(this.scene, 0, 0, CharacterSkins.Grizzly);
    this.character.addToUpdateList();
    this.add(this.character);
    this.character.refresh();

    this.setSize(this.character.displayWidth, this.character.displayHeight);

    this.isAlive = true;
    this.isLocked = false;

    this.isMoving = false;

    this.canBreakAllSmashables = RunSettingsHandler.canBreakAllSmashables(this.scene);

    this.actionState = ActionState.Grounded;

    this.globalSpeed = RunSettingsHandler.getGlobalSpeed(this.scene);
    this.speed = speed * 16.66;
    this.maxFallSpeed = 500;
    this.glideSpeedModifier = 0.85;
    this.glideMaxFallSpeed = 0.5;
    this.maxSpeed = 0.5 * 16.66;
    

    this.maxGlideTime = RunSettingsHandler.getGlideTime(this.scene);
    this.glideTime = this.maxGlideTime;

    this.isTouchingFloor = false;
    this.groundTouchPoints = 0;
    this.platformsTouching = [];

    // Jumping
    this.maxJumpTime = 1000;
    this.numJumps = 0;
    this.jumpTimer = 0;
    this.jumpCooldownTime = 0; //100;
    this.jumpCooldownTimer = 0;

    // Smashing
    this.isSmashing = false;
    this.maxSmashTime = 1000;
    this.smashTimer = 0;

    // Dropping
    this.droppingGraceTime = 250;
    this.droppingTimer = this.droppingGraceTime * (1 / this.globalSpeed);

    this.magnetStrength = RunSettingsHandler.getMagnetStrength(this.scene);
    this.magnetisedGems = new Set();

    this.injectMatter(this);
    // this.configureMatter();

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      this.scene.registry.events.off(RunSettingsHandler.events.ChangeGlobalSpeed, this.onGlobalSpeedChange, this);
    });

    this.scene.events.once(CharacterSpine.events.StopCharacter, this.stopMoving, this);
    RunSettingsHandler.onGlobalSpeedChange(this.scene, this.onGlobalSpeedChange, this);
  }

  get IsAlive() {
    return this.isAlive;
  }

  get IsMoving() {
    return this.isMoving;
  }

  get CurrentCharacter() {
    return this.character.CurrentSkin;
  }

  get CanBreakAllSmashables() {
    return this.canBreakAllSmashables;
  }

  public resetPosition(x: number, y: number) {
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
  }

  public shiftPosition(x: number, y: number) {
    console.log(`Shifting player from ${this.x}, ${this.y} to ${x}, ${y}`);
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
  }

  // public setRelicEffects() {
  //   this.maxGlideTime = RunSettingsHandler.getGlideTime(this.scene);
  //   this.glideTime = this.maxGlideTime;
  //   this.canBreakAllSmashables = RunSettingsHandler.canBreakAllSmashables(this.scene);
  //   this.magnetStrength = RunSettingsHandler.getMagnetStrength(this.scene);
  // }

  public startMoving() {
    console.log('Moving');
    this.isMoving = true;
    this.character.startMoving();
    this.isLocked = false;
  }

  public stopMoving() {
    console.log('Stopping');
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.isLocked = true;
    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
  }

  public freeze() {
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
    this.isLocked = true;
  }

  public startSmash() {
    console.log('Start Smash!');
    if (this.actionState != ActionState.Grounded) return;

    if (this.isSmashing) {
      this.smashTimer += this.maxSmashTime * (1 / this.globalSpeed);
    } else {
      this.character.startSmash();
      this.smashTimer = this.maxSmashTime * (1 / this.globalSpeed);
      this.isSmashing = true;
    }
  }

  public endSmash() {
    console.log('End Smash!');

    this.isSmashing = false;
    this.character.endSmash();
  }

  public jumpStartAction() {
    if (this.isLocked) return;

    if (this.canJump()) {
      this.startJump();
    } else if (this.actionState == ActionState.Falling) {
      this.startGliding();
    }
  }

  public jumpEndAction() {
    if (this.isLocked) return;

    if (this.actionState == ActionState.Jumping) {
      this.endJump();
    } else if (this.actionState == ActionState.Gliding) {
      this.stopGliding();
    }
  }

  public switchAction() {
    if (this.isLocked) return;

    this.character.switchCharacter();

    this.scene.events.emit(Player.events.SwitchedCharacter, this.CurrentCharacter);
    app.audioHandler.playSound(this.scene, 'SndBearSwitch', 'sfx');
  }

  public canJump(): boolean {
    let hasRemainingJumps = this.numJumps < RunSettingsHandler.getMaxJumps(this.scene);

    if (this.actionState == ActionState.Jumping) {
      return this.jumpCooldownTimer <= 0 && hasRemainingJumps;
    } else {
      // return this.isTouchingFloor || this.numJumps < RunSettingsHandler.getMaxJumps(this.scene);
      return this.isTouchingFloor || hasRemainingJumps;
    }
  }

  public kill() {
    if (!this.isAlive) return;

    this.playerDeathLayer.add(this);

    // Make sure any gems currently travelling toward the player are hidden.
    for (const gem of this.magnetisedGems) {
      // gem.alpha = 0;
      gem.collect();
    }

    this.platformsTouching = [];
    this.magnetisedGems.clear();

    this.stopMoving();
    this.character.die();
    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
    (this.body as MatterJS.BodyType).gravityScale.y = 0;
    (this.body as MatterJS.BodyType).collisionFilter = {
      group: -1,
      category: (this.body as MatterJS.BodyType).collisionFilter.category,
      mask: 0
    };
    // (this.body as MatterJS.BodyType).isSensor = true;

    this.isAlive = false;

    this.scene.add.tween({
      targets: this,
      y: '-= 300',
      // scale: 1.5,
      duration: 1000,
      ease: Phaser.Math.Easing.Circular.Out
    });

    // let distance = 750 - this.y;
    this.scene.add.tween({
      targets: this,
      y: 750,
      duration: 1000,
      ease: Phaser.Math.Easing.Circular.InOut,
      delay: 1000
    });
  }

  public respawn(x: number, y: number) {
    this.isAlive = true;
    this.playerLayer.add(this);
    // this.setScale(1);
    this.actionState = ActionState.Grounded;
    this.resetPosition(x, y);
    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, false);
    (this.body as MatterJS.BodyType).gravityScale.y = 1;
    (this.body as MatterJS.BodyType).collisionFilter = {
      group: 0,
      category: (this.body as MatterJS.BodyType).collisionFilter.category,
      mask: 0xffffffff
    };

    this.alpha = 0;
    this.scene.add.tween({
      targets: this,
      alpha: 1,
      duration: 1000,
      ease: Phaser.Math.Easing.Cubic.Out
    });
    // (this.body as MatterJS.BodyType).isSensor = false;
  }

  public update(time: number, delta: number) {
    if (this.isSmashing) {
      this.smashTimer -= delta;

      if (this.smashTimer <= 0) this.endSmash();
    }

    if (this.actionState == ActionState.Jumping) {
      this.jumpTimer -= delta;
      this.jumpCooldownTimer -= delta;

      if (this.jumpTimer <= 0) {
        this.endJump();
      }
    } else if (this.actionState == ActionState.Gliding) {
      this.glideTime -= delta;
      if (this.glideTime <= 0) {
        this.actionState = ActionState.Falling;
        this.character.falling();
      }
    }
  }

  public physicsUpdate(time: number, delta: number) {
    if (this.isMoving) {
      let moveSpeedX = this.speed * this.globalSpeed;
      let moveSpeedY = this.body.velocity.y;
      if (this.actionState == ActionState.Gliding) {
        moveSpeedX *= this.glideSpeedModifier;
        moveSpeedY = Math.min(this.glideMaxFallSpeed * (1 / this.globalSpeed), moveSpeedY);
      }
      moveSpeedY = Math.min(this.maxFallSpeed, moveSpeedY);
      if (moveSpeedY > 1) console.log('Y move speed', moveSpeedY);
      this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: moveSpeedX, y: moveSpeedY });
    }

    if (this.actionState == ActionState.Dropping && this.droppingTimer > 0) {
      this.droppingTimer -= delta;

      if (this.droppingTimer <= 0) {
        console.log('Flying!');
        this.actionState = ActionState.Flying;
        this.character.falling();
      }
    }

    if (
      this.actionState != ActionState.Falling &&
      this.actionState != ActionState.Gliding &&
      this.actionState != ActionState.Dropping &&
      !this.isTouchingFloor &&
      this.isAlive &&
      this.body.velocity.y > 0
    ) {
      if (this.actionState == ActionState.Jumping) {
        this.jumpTimer = 0;
        this.endJump();
        this.startGliding();
      } else {
        this.actionState = ActionState.Falling;
        this.character.falling();
      }
    }

    let moveDistance = 1 * delta;
    for (const gem of this.magnetisedGems) {
      if (!gem.active || gem.HasCollided) {
        this.magnetisedGems.delete(gem);
        continue;
      }

      let distanceToPlayer = Phaser.Math.Distance.Between(gem.x, gem.y, this.x, this.y);
      if (distanceToPlayer > moveDistance) {
        let progress = moveDistance / distanceToPlayer;
        gem.x = Phaser.Math.Interpolation.Linear([gem.x, this.x], progress);
        gem.y = Phaser.Math.Interpolation.Linear([gem.y, this.y], progress);
      } else {
        gem.x = this.x;
        gem.y = this.y;
      }
    }
  }

  public addMagnetisedGem(gem: EntityGem) {
    if (!this.magnetisedGems.has(gem)) this.magnetisedGems.add(gem);
  }

  protected onGlobalSpeedChange(parent: any, value: any, previousValue: any) {
    this.globalSpeed = value;
  }

  public celebrate(){
    /*this.actionState = ActionState.Jumping;
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    // this.scene.matter.applyForceFromAngle(this.body as MatterJS.BodyType, 0.75 * (1 / this.globalSpeed), 4.712389);
    this.scene.matter.applyForceFromAngle(this.body as MatterJS.BodyType, 0, 4.712389);
    this.character.startJump();
    this.scene.events.emit(Player.events.Jumped);*/

    this.platformsTouching = [];
    this.magnetisedGems.clear();

    this.stopMoving();
    
    (this.body as MatterJS.BodyType).gravityScale.y = 0;
    (this.body as MatterJS.BodyType).collisionFilter = {
      group: -1,
      category: (this.body as MatterJS.BodyType).collisionFilter.category,
      mask: 0
    };
    // (this.body as MatterJS.BodyType).isSensor = true;

   // this.isAlive = false;

    this.character.startJump();
    this.scene.add.tween({
      targets: this,
      y: '-= 50',
      duration: 150,
      ease: Phaser.Math.Easing.Circular.Out
    });

    this.scene.time.delayedCall(150, () => {
      this.character.falling();
      this.scene.add.tween({
        targets: this,
        y: '+= 50',
        duration: 130,
        ease: Phaser.Math.Easing.Circular.In
      });
    });
    this.scene.time.delayedCall(300, () => {
      this.character.startJump();
      this.scene.add.tween({
        targets: this,
        y: '-= 50',
        duration: 150,
        ease: Phaser.Math.Easing.Circular.Out
      });
    });
    this.scene.time.delayedCall(450, () => {
      this.character.falling();
      this.scene.add.tween({
        targets: this,
        y: '+= 50',
        duration: 130,
        ease: Phaser.Math.Easing.Circular.In
      });
    });
    this.scene.time.delayedCall(600, () => {
      this.character.startJump();
      this.scene.add.tween({
        targets: this,
        y: '-= 50',
        duration: 150,
        ease: Phaser.Math.Easing.Circular.Out
      });
    });
    this.scene.time.delayedCall(750, () => {
      this.character.falling();
      this.scene.add.tween({
        targets: this,
        y: '+= 50',
        duration: 130,
        ease: Phaser.Math.Easing.Circular.In
      });
    });
    this.scene.time.delayedCall(900, () => {
      this.character.stopMoving();
      this.actionState = ActionState.Grounded;
      this.resetPosition(this.x, this.y);
    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, false);
      (this.body as MatterJS.BodyType).gravityScale.y = 1;
      (this.body as MatterJS.BodyType).collisionFilter = {
        group: 0,
        category: (this.body as MatterJS.BodyType).collisionFilter.category,
        mask: 0xffffffff
      };
    });


    

    
  }

  protected startJump() {
    console.log('Jump!');
    this.actionState = ActionState.Jumping;

    this.glideTime = this.maxGlideTime;
    this.jumpTimer = this.maxJumpTime * (1 / this.globalSpeed);
    this.jumpCooldownTimer = this.jumpCooldownTime;
    this.numJumps++;

    this.character.startJump();

    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: this.speed, y: 0 });
    // this.scene.matter.applyForceFromAngle(this.body as MatterJS.BodyType, 0.75 * (1 / this.globalSpeed), 4.712389);
    this.scene.matter.applyForceFromAngle(this.body as MatterJS.BodyType, 0.75, 4.712389);

    this.scene.events.emit(Player.events.Jumped);
    var value = Phaser.Math.Between(1, 2);
    app.audioHandler.playSound(this.scene, 'SndJump' + value, 'sfx');
  }

  protected endJump() {
    console.log('Jump Finish!');
    this.actionState = ActionState.Flying;

    this.jumpTimer = Math.max(this.jumpTimer, 0);
    // this.scene.matter.applyForceFromAngle(
    //   this.body as MatterJS.BodyType,
    //   0.25 * (1 / this.globalSpeed) + 0.0005 * (1 / this.globalSpeed) * this.jumpTimer,
    //   1.570796
    // );
    this.scene.matter.applyForceFromAngle(this.body as MatterJS.BodyType, 0.25 * this.globalSpeed + 0.0005 * this.globalSpeed * this.jumpTimer, 1.570796);
    console.log('End Jump Force', 0.25 * this.globalSpeed + 0.0005 * this.globalSpeed * this.jumpTimer);
  }

  protected startGliding() {
    console.log('Gliding!');
    if (this.glideTime > 0) {
      this.actionState = ActionState.Gliding;

      this.character.gliding();
    } else {
      this.actionState = ActionState.Falling;
      this.character.falling();
    }

    // this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: this.speed, y: 0 });
    // (this.body as MatterJS.BodyType).gravityScale.x = 0.1;
    // (this.body as MatterJS.BodyType).gravityScale.y = 0.1;
  }

  protected stopGliding() {
    console.log('Gliding Finish!');
    this.actionState = ActionState.Falling;
    this.character.falling();

    // (this.body as MatterJS.BodyType).gravityScale.x = 1;
    // (this.body as MatterJS.BodyType).gravityScale.y = 1;
  }

  protected injectMatter(target: Phaser.GameObjects.GameObject) {
    let x = this.x;
    let y = this.y;

    this.matterSprite = this.scene.matter.add.gameObject(target) as Phaser.Physics.Matter.Sprite;

    this.playerRect = this.scene.matter.bodies.rectangle(0, 0, this.character.displayWidth, this.character.displayHeight, {
      label: BodyTypes.Player,
      onCollideCallback: this.onPlayerCollisionStart.bind(this),
      onCollideEndCallback: this.onPlayerCollisionEnd.bind(this)
    });

    /*let feetSensor = this.scene.matter.bodies.rectangle(0, this.character.displayHeight * 0.5 + 5, this.character.displayWidth - 10, 10, {
      label: BodyTypes.Player,
      // label: 'Feet Sensor',
      isSensor: true,
      onCollideCallback: this.onFeetCollisionStart.bind(this),
      onCollideActiveCallback: this.onFeetCollision.bind(this),
      onCollideEndCallback: this.onFeetCollisionEnd.bind(this)
    });*/

    this.gemMagnet = this.scene.matter.bodies.circle(0, 0, this.magnetStrength, {
      label: BodyTypes.GemMagnet,
      isSensor: true,
      density: 0
    });

    let compoundBody = this.scene.matter.body.create({
      label: BodyTypes.Player,
      parts: this.magnetStrength > 0 ? [this.playerRect, this.gemMagnet] : [this.playerRect],
      restitution: 0,
      inertia: Infinity
    });

    compoundBody.position.y = this.character.displayOriginY * this.character.scaleY * 0.5;

    this.matterSprite.setExistingBody(compoundBody);

    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, this.scene.matter.vector.create(x, y));
    this.scene.matter.setVelocity(this.body as MatterJS.BodyType, 0, 0);
  }

  /*
  protected onFeetCollisionStart(pair: MatterJS.ICollisionPair) {
    console.log('Collision Start', pair.id);

    if (
         (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ground ||
         (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ground ||
         (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Platform ||
         (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Platform
       ) {
      //   // console.log(pair.collision.normal);
         this.groundTouchPoints++;

    }
    // if (
    //   (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ground ||
    //   (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ground ||
    //   (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Platform ||
    //   (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Platform
    // ) {
    //   // console.log(pair.collision.normal);
    //   this.groundTouchPoints++;

    //   if (this.groundTouchPoints == 1) {
    //     console.log('Grounded');

    //     if (this.actionState == ActionState.Gliding) this.stopGliding();
    //     this.character.land();
    //   }

    //   this.isTouchingFloor = true;
    //   this.actionState = ActionState.Grounded;
    //   this.numJumps = 0;
    // }
  }

  protected onFeetCollision(pair: MatterJS.IPair) {
    // console.log('Collision ongoing');
  }

  protected onFeetCollisionEnd(pair: MatterJS.IPair) {
    console.log('Collision End', pair.id);

    if (
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ground ||
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ground ||
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Platform ||
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Platform
    ) {
      this.groundTouchPoints--;

      //if (this.groundTouchPoints <= 0) {

      if(this.platformsTouching.length<=0){
        console.log('No ground');

        this.isTouchingFloor = false;
        this.groundTouchPoints = 0;

        if (this.actionState == ActionState.Grounded) {
          console.log('Flying!');
          this.actionState = ActionState.Flying;
          this.character.falling();
        }
      }
    }
  }
*/
  protected onPlayerCollisionStart(pair: MatterJS.ICollisionPair) {
    console.log(`Player Collision! ${pair.id}`, pair.collision.normal);

    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ground || (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Platform) {
      this.onPlatformCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ground || (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Platform) {
      this.onPlatformCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    }
  }

  protected onPlayerCollisionEnd(pair: MatterJS.ICollisionPair) {
    console.log(`Player Collision End! ${pair.id}`);

    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ground || (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Platform) {
      this.onPlatformCollisionEnd(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ground || (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Platform) {
      this.onPlatformCollisionEnd(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    }
  }

  protected onPlatformCollisionStart(pair: MatterJS.ICollisionPair, player: MatterJS.BodyType, platform: MatterJS.BodyType) {
    if (pair.collision.normal.y < 0) {
      console.log('Grounded!');

      //this.groundTouchPoints++;

      const index = this.platformsTouching.indexOf(pair.id, 0);
      if (index < 0) {
        this.platformsTouching.push(pair.id);
      }

      if (this.platformsTouching.length >= 1) {
        console.log('Grounded');

        if (this.actionState == ActionState.Gliding) this.stopGliding();
        this.character.land();
      }

      this.isTouchingFloor = true;
      this.actionState = ActionState.Grounded;
      this.numJumps = 0;
    } else {
      pair.isSensor = true;
    }
  }

  protected onPlatformCollisionEnd(pair: MatterJS.ICollisionPair, player: MatterJS.BodyType, platform: MatterJS.BodyType) {
    pair.isSensor = false;

    const index = this.platformsTouching.indexOf(pair.id, 0);
    if (index > -1) {
      this.platformsTouching.splice(index, 1);
    }
    console.log(`Player Collision Number! ${this.platformsTouching.length}`);
    if (this.platformsTouching.length <= 0) {
      this.isTouchingFloor = false;
      this.groundTouchPoints = 0;

      if (this.actionState == ActionState.Grounded) {
        // console.log('Flying!');
        // this.actionState = ActionState.Flying;
        // this.character.falling();

        console.log('Dropping!');
        this.actionState = ActionState.Dropping;
        this.droppingTimer = this.droppingGraceTime * (1 / this.globalSpeed);
      }
    }
  }
}
